import React, { useState, useEffect, useMemo } from "react"
import { Input, Button, Select, message, Spin, Popconfirm } from "antd"
import { PlusOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons"
import { secureApi } from "../../Config/api"
import { GET, POST } from "../../utils/apis"
import { useDispatch, useSelector } from "react-redux"
import { fetchEstimations, fetchStats } from "../../utils/helpers"
import { translations } from "../../utils/constants"
import { setEstimationData } from "../../Redux/actions/estimationActions"

const DeviceManagement = ({ vehicleId, language }) => {
    const user = useSelector((state) => state.authReducer?.user)
    const rawData = useSelector((state) => state.estimation.data)
    const dispatch = useDispatch()
    const [systemName, setSystemName] = useState("")
    const [systemColor, setSystemColor] = useState("blue")
    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(true)
    const [editingId, setEditingId] = useState(null)
    const [editValue, setEditValue] = useState("")

    const colorOptions = [
        { label: translations[language]?.blue, value: "blue" },
        { label: translations[language]?.green, value: "green" },
        { label: translations[language]?.purple, value: "purple" },
        { label: translations[language]?.red, value: "red" },
        { label: translations[language]?.orange, value: "orange" },
        { label: translations[language]?.gray, value: "gray" }
    ];

    const systems = useMemo(() => {
        const map = new Map()
        rawData?.forEach(r => {
            if (!map.has(r.device_id)) {
                map.set(r.device_id, {
                    id: r.device_id,
                    name: r.device_name,
                    color: r.device_color,
                    estimationId: r.device_quantity_id
                })
            }
        })
        return Array.from(map.values())
    }, [rawData])

    useEffect(() => {
        const fetchSystems = async () => {
            try {
                setFetching(true)
                const res = await secureApi.get(GET.GET_ESTIMATION_DATA, {
                    params: { user_id: user?.id, vehicle_id: vehicleId }
                })
                if (res?.success) {
                    dispatch(setEstimationData(res.data))
                } else {
                    message.error(res?.message || translations[language]?.fetchDeviceError)
                }
            } catch (err) {
                console.error("fetchSystems error:", err)
                message.error(translations[language]?.fetchDeviceError)
            } finally {
                setFetching(false)
            }
        }
        if (user?.id && vehicleId) fetchSystems()
    }, [user?.id, vehicleId, dispatch, language])

    const handleAddSystem = async () => {
        if (!systemName.trim()) {
            return message.error(translations[language]?.deviceNameRequired)
        }

        try {
            setLoading(true)
            const payload = {
                columnName: systemName,
                color: systemColor,
                vehicle_id: vehicleId
            }
            const res = await secureApi.post(POST.ESTIMATION_ADD_COLUMN, payload)

            if (res?.success) {
                setSystemName("")
                setSystemColor("blue")
                message.success(translations[language]?.deviceAdded)
                fetchEstimations({ user, dispatch, vehicleId })
                fetchStats({ dispatch, user, vehicleId })
            } else {
                message.error(res?.message || translations[language]?.deviceAddError)
            }
        } catch (err) {
            console.error("Add device error:", err)
            message.error(translations[language]?.deviceAddError)
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (sys) => {
        try {
            setLoading(true)
            const payload = {
                vehicle_id: vehicleId,
                device_id: sys.id
            }
            const res = await secureApi.post(POST?.DELETE_DEVICE, payload)
            // console.log(res, 'res')
            if (res?.success) {
                message.success(translations[language]?.deviceDeleted)
                fetchEstimations({ user, dispatch, vehicleId })
                fetchStats({ dispatch, user, vehicleId })
            } else {
                message.error(res?.message || translations[language]?.deviceDeleteError)
            }
        } catch (err) {
            console.error("Delete device error:", err)
            message.error(translations[language]?.deviceDeleteError)
        } finally {
            setLoading(false)
        }
    }

    const handleEditClick = (sys) => {
        setEditingId(sys.id)
        setEditValue(sys.name)
    }

    const handleSaveEdit = async (sys) => {
        if (!editValue.trim()) {
            message.error(translations[language]?.deviceNameRequired || "Please enter a device name")
            return
        }

        try {
            const payload = {
                vehicle_id: vehicleId,
                device_id: sys.id,
                name: editValue
            }
            const res = await secureApi?.post(POST?.UPDATE_DEVICE_NAME, payload)
            // console.log(res, 'res')
            if (res?.success) {
                setEditingId(null)
                message.success(translations[language]?.deviceUpdated || "Device name updated")
                fetchEstimations({ user, dispatch, vehicleId })
                fetchStats({ dispatch, user, vehicleId })
            } else {
                message.error(
                    res?.message ||
                    translations[language]?.deviceUpdateError ||
                    "Update failed"
                )
            }
        } catch (err) {
            console.error("Update device name error:", err)
            message.error(translations[language]?.deviceUpdateError || "Update failed")
        }
    }

    if (fetching) {
        return (
            <div className="loading-container">
                <Spin size="large" />
            </div>
        )
    }

    // console.log('systemName',)
    return (
        <div className="systems-container">
            <div className="card form-card">
                <h3>{translations[language]?.addNewDevice}</h3>
                <p>{translations[language]?.addDeviceSubtitle}</p>

                <div className="system-input-inner-div">
                    <div className="system-input-div">
                        <label>{translations[language]?.deviceName}</label>
                        <Input
                            placeholder={translations[language]?.devicePlaceholder}
                            value={systemName}
                            onChange={(e) => setSystemName(e.target.value)}
                            className="antd-input"
                        />
                    </div>
                    <div className="system-input-div-second">
                        <label>{translations[language]?.color}</label>
                        <Select
                            value={systemColor}
                            onChange={setSystemColor}
                            style={{ width: "100%" }}
                            options={colorOptions}
                            className="antd-select"
                        />
                    </div>
                </div>

                <div className="button-main-div">
                    <Button
                        className="dashboard-export"
                        type="primary"
                        loading={loading}
                        icon={<PlusOutlined />}
                        onClick={handleAddSystem}
                        style={{ marginTop: 22 }}
                    >
                        {translations[language]?.addDevice}
                    </Button>
                </div>
            </div>

            {/* Current devices list */}
            <div className="card list-card">
                <h3>{translations[language]?.currentDevice}</h3>
                <p>{translations[language]?.currentDeviceSubtitle}</p>

                <div className="system-list">
                    {systems.length === 0 ? (
                        <p>{translations[language]?.noDeviceFound}</p>
                    ) : (
                        systems.map(sys => (
                            <div
                                key={sys.id}
                                className="system-item"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                    background: "#f6fbff",
                                    borderRadius: 6,
                                    marginBottom: 8
                                }}
                            >
                                {/* LEFT: dot + name/input */}
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span className="dot" style={{ backgroundColor: sys.color }} />
                                    {editingId === sys.id ? (
                                        // <Input
                                        //     size="small"
                                        //     value={editValue}
                                        //     onChange={(e) => setEditValue(e.target.value)}
                                        //     style={{ width: 200 }}
                                        // />
                                        <Input
                                            size="small"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            style={{
                                                width: Math.max(200, editValue.length * 8) + "px", // dynamic width
                                                transition: "width 0.2s ease"
                                            }}
                                            className="device-edit-input"
                                        />

                                    ) : (
                                        <span className="system-name">{sys.name}</span>
                                    )}
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    {editingId === sys.id ? (
                                        <>
                                            <CheckOutlined
                                                style={{ color: "green", cursor: "pointer" }}
                                                onClick={() => handleSaveEdit(sys)}
                                            />
                                            <CloseOutlined
                                                style={{ color: "red", cursor: "pointer" }}
                                                onClick={() => setEditingId(null)}
                                            />
                                        </>
                                    ) : (
                                        <EditOutlined
                                            style={{ color: "#1890ff", cursor: "pointer" }}
                                            onClick={() => handleEditClick(sys)}
                                        />
                                    )}

                                    <Popconfirm
                                        title={translations[language]?.deleteMsg}
                                        okText={translations[language]?.yes}
                                        cancelText={translations[language]?.no}
                                        onConfirm={() => handleDelete(sys)}
                                    >
                                        <DeleteOutlined
                                            style={{ color: "red", cursor: "pointer" }}
                                        // onClick={}
                                        />
                                    </Popconfirm>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}

export default DeviceManagement
